// Maybe temporary, could in theory be combined with filtered orders blotter

import { BLOTTER_ID_OPEN_ORDERS } from '@src/containers/Blotters/Orders/tokens';
import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import { useCurrentAppLayoutId } from '@src/hooks/useAppLayoutId';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useNavigateToTradingOrderDetails } from '@src/hooks/useNavigateToTradingOrderDetails';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import {
  Accordion,
  AccordionBody,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterDensity,
  BlotterTable,
  BlotterTablePauseButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  FilterBuilder,
  FilterBuilderClearAllButton,
  FilterBuilderToggleButton,
  FilterInput,
  HStack,
  MixpanelEvent,
  Portal,
  SummaryLine,
  filterByColumnMainMenuItems,
  getOrderAmount,
  getOrderQuantity,
  isGridApiReady,
  useAccordionFilterBuilder,
  useBlotterTable,
  useMixpanel,
  usePersistedBlotterTable,
  type Order,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { useCallback, useState } from 'react';
import { usePredictionMarketBlotterColumns } from '../predictionMarketBlotter';
import { useOrderColumns } from './Columns';
import { OrderOptions } from './OrderOptions';
import { useOpenOrdersBlotterObs } from './useOpenOrdersBlotterObs';
import { colIDToFilterBuilderKey, useOrderFilter, type BlotterTableOrderFilter } from './useOrderFilter';
import { useOrderMenu } from './useOrderMenu';

interface OpenOrdersParams {
  defaultFilter: BlotterTableOrderFilter;
}

export function OpenOrders({ defaultFilter }: OpenOrdersParams) {
  const mixpanel = useMixpanel();
  const { dataObservable: liveOrders, errorObservable: recentOrdersError } = useOpenOrdersBlotterObs();
  const layoutId = useCurrentAppLayoutId();
  const { homeCurrency } = useDisplaySettings();
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  const predictionColumns = usePredictionMarketBlotterColumns();
  const columns = useOrderColumns({ additionalColumns: predictionColumns });

  const persisted = usePersistedBlotterTable(BLOTTER_ID_OPEN_ORDERS, { filter: defaultFilter });

  const filteredOrders = useOrderFilter({
    initialFilter: persisted.initialState?.filter,
    saveFilter: persisted.onFilterChanged,
    includeStatusFilter: false,
    includeIDFilter: false,
  });
  const { clientSideFilter, changeFilter, filterableProperties, filterBuilderProps } = filteredOrders;

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Order>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const selectedRows = api.getSelectedRows();
    setSelectedOrders(selectedRows);
  }, []);

  const { filterBuilder, accordion, openClause } = useAccordionFilterBuilder({
    accordionProps: {},
    filterBuilderProps,
  });

  const { activeFilterCount, removeAllFilterClauses } = filterBuilder;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );

  const { getCustomContextMenuItems, dialogComponents } = useOrderMenu({
    openClause,
    filterableProperties,
    generateOrderDetailsRoute: getTradingOrderDetailsRoute,
    layoutId,
  });

  const { showBlotterPauseButton } = useFeatureFlag();
  const onOpenDetails = useNavigateToTradingOrderDetails();

  const blotterTable = useBlotterTable<Order>({
    dataObservable: liveOrders,
    errorObservable: recentOrdersError,
    rowID: 'OrderID',
    density: BlotterDensity.Compact,
    clientLocalFilter: clientSideFilter,
    getExtraMainMenuItems,
    persistence: persisted,
    columns,
    contextMenuOptions: { getCustomContextMenuItems },
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    gridOptions: {
      // There are a few heavy valueGetters in this blotter. Enabling the value cache improves performance.
      valueCache: true,
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      onRowDoubleClicked: params => {
        const data = params.data;
        if (data) {
          onOpenDetails(data.OrderID, 'order', 'details');
        }
      },
    },
    exportOptions: {
      name: 'OpenOrders',
    },
  });

  return (
    <>
      <Portal portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}>
        <HStack justifyContent="space-between" gap="spacing4">
          <FilterInput
            alwaysExpanded
            value={blotterTable.blotterTableFiltersProps.quickFilterText}
            onChange={val => blotterTable.blotterTableFiltersProps.onQuickFilterTextChanged(val)}
            mixpanelEvent={MixpanelEvent.FilterQuickSearch}
            tooltip="Filter orders"
          />
          <Divider orientation="vertical" m="spacing4" />
          <FilterBuilderClearAllButton
            removeAllFilterClauses={removeAllFilterClauses}
            disabled={activeFilterCount === 0}
          />
          <FilterBuilderToggleButton
            activeFilterCount={activeFilterCount}
            isOpen={accordion.isOpen}
            onClick={() => accordion.toggle()}
          />
          <Divider orientation="vertical" m="spacing4" />
          {blotterTable.blotterTableFiltersProps.showPauseButton && (
            <>
              <BlotterTablePauseButton
                paused={blotterTable.blotterTableFiltersProps.paused}
                pause={blotterTable.blotterTableFiltersProps.pause}
                resume={blotterTable.blotterTableFiltersProps.resume}
              />
              <Divider orientation="vertical" m="spacing4" />
            </>
          )}

          <OrderOptions
            changeFilter={changeFilter}
            filter={filteredOrders.filter}
            blotterTable={blotterTable}
            tabType="OpenOrders"
          />
        </HStack>
      </Portal>
      <Accordion {...accordion}>
        <AccordionBody>
          <FilterBuilder {...filterBuilder} />
        </AccordionBody>
      </Accordion>
      <BlotterTable {...blotterTable} rowEndSpacing="spacing12" />
      <SummaryLine
        homeCurrency={homeCurrency}
        rows={selectedOrders}
        getQuantity={getOrderQuantity}
        getAmount={getOrderAmount}
      />
      {dialogComponents}
    </>
  );
}
